<template>
    <div class="modal-card card">
        <form @submit.prevent="onAddCredential()">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">
                    Add Credential
                </h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="card-body">
                <div class="form-group">
                    <div class="d-flex justify-content-between align-items-center">
                        <label class="form-label">Credential Type <span class="text-danger">&nbsp;*</span></label>
                        <div class="d-flex justify-content-end align-items-center gap-2"
                            v-if="credentialType?.name == 'aws'">
                            <span class="text-muted form-label">Supported Combinations</span>
                            <span class="text-muted form-label" @mouseover="showCustomTooltip"
                                @mouseout="hideCustomTooltip">
                                <i class="fa fa-info-circle" aria-hidden="true"></i>
                            </span>
                        </div>
                    </div>
                    <select class="form-select" @change="onSelectCredentialType($event)" v-model="credTypeName">
                        <option v-for="option in this.credentialOptions" :key="option.index" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </div>
                <FormFields :fields="fields" v-model="credential" />
                <div v-if="error" class="text-danger">
                    {{ error }}
                </div>
            </div>
            <div class="card-footer">
                <div class="text-end">
                    <SubmitButton :fields="fields" v-model="credential" :loading="loading" />
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import FormFields from '../FormFields.vue';
import SubmitButton from '../SubmitButton.vue';
import { mapActions } from 'vuex';
import { isDuplicate } from '../../utils/commonFunction';
export default {
    components: { FormFields, SubmitButton },
    data() {
        return {
            error: "",
            credential: {},
            loading: false
        }
    },
    computed: {
        fields() {
            let currentCredentialType = this.credentialType;
            let fields = [{
                name: "name",
                type: "text",
                label: "Credential Name",
                placeholder: "Enter name",
                validation: [{ type: "required" },
                {
                    type: "regex", pattern: "^[a-zA-Z0-9_]+$",
                    errMsg: "Credential name may contain only alphabets or numbers"
                }]
            }]
            if (currentCredentialType) {
                currentCredentialType.fields.map((value, index) => {
                    fields.push({
                        name: value.name,
                        type: "text",
                        label: value.name,
                        editable: true,
                        url: value.type == "url" ? "https://onetimesecret.com/" : null,
                        placeholder: value.description,
                        toolTip: value.type == "url" ? "Do not directly enter your secret here. use the onetimesecret url generated using the 'key'" : null,
                        validation: value.required ? [{ type: "required" }] : []
                    })
                    if (value.type == 'url') {
                        fields[index + 1].validation.push({
                            type: "regex", pattern: `^https:\\/\\/(?:[a-zA-Z0-9-]+\\.)?onetimesecret\\.com\\/secret\\/(?:[a-zA-Z0-9-]+)*$`,
                            errMsg: "Onetimesecret URL is required"
                        })
                    }

                })
            }

            // if aws credential type is selected, remove required for all fields
            if (this.credentialType?.name == 'aws') {
                fields.forEach(field => {
                    // remove required type validation for all fields except credential name
                    if (field.name != 'name') field.validation = [];
                });
            }
            return fields
        },
        credentialType() {
            return this.$store.state.amdp.currentCredentialType
        },
        credentialTypes() {
            return this.$store.state.amdp.credentialTypes
        },
        credentialOptions() {
            let types = this.$store.state.amdp.credentialTypes
            let options = types.map((type) => {
                return {
                    label: type.name,
                    value: type.name,
                }
            })
            return options
        },
        credTypeName: {
            get() {
                return this.credentialType ? this.credentialType.name : ""
            },
            set(value) {

            }
        }
    },
    name: "AddCredential",
    beforeUnmount() {
        // Clean up any existing tooltips when component is unmounted
        this.hideCustomTooltip();
    },
    methods: {
        ...mapActions({
            createCredential: "amdp/createCredential",
            showModal: "showModal"
        }),
        onSelectCredentialType(event) {
            let credtype = this.credentialTypes.find(type => {
                return type.name == event.target.value
            });
            this.$store.dispatch("amdp/setCurrentCredentialType", credtype);
        },
        showCustomTooltip(event) {
            if (this.credentialType?.name !== 'aws') {
                return;
            }

            // Remove any existing tooltips
            this.hideCustomTooltip();

            const tooltip = document.createElement("div");
            const combinations = [
                "access_key, secret_key",
                "access_key, secret_key, session_token",
                "role_arn",
                "role_arn, external_id",
                "access_key, secret_key, role_arn",
                "access_key, secret_key, role_arn, external_id",
                "access_key, secret_key, session_token, role_arn",
                "access_key, secret_key, session_token, role_arn, external_id"
            ]

            // Generate the list items dynamically
            const listItems = combinations.map((combo, index) =>
                `<li class="combo-item">${combo}</li>`
            ).join('');

            tooltip.innerHTML = `
                <div class="custom-tooltip">
                    <div class="custom-tooltip-content">
                        <strong>Only the following credential field combinations are supported for AWS. Any other combination will result in validation errors</strong>
                        <ol class="mt-3">
                            ${listItems}
                        </ol>
                    </div>
                </div>
            `;
            tooltip.className = "credential-tooltip";

            // Get the trigger element position
            const triggerElement = event.target;
            const rect = triggerElement.getBoundingClientRect();

            // Position tooltip centered below the trigger element
            tooltip.style.left = (rect.left + rect.width / 2) + "px";
            tooltip.style.top = (rect.bottom + 8) + "px";
            tooltip.style.transform = "translateX(-50%)"; // Center horizontally

            document.body.appendChild(tooltip);
        },
        hideCustomTooltip() {
            document.querySelectorAll(".credential-tooltip").forEach(tooltip => tooltip.remove());
        },
        async onAddCredential() {
            if (!isDuplicate(this.credential.name, this.$store.state.amdp.credentials, "name", null)) {

                // AWS credential type checks
                if (this.credentialType?.name == 'aws') {
                    // check if atleast one field is filled
                    const awsFields = ['access_key', 'secret_key', 'role_arn', 'session_token', 'external_id'];
                    const hasAtLeastOneField = awsFields.some(fieldName => {
                        return this.credential[fieldName] && this.credential[fieldName].trim() !== '';
                    });

                    if (!hasAtLeastOneField) {
                        this.error = "At least one of the following AWS credential fields must be provided: access_key, secret_key, role_arn, session_token, external_id. Check the supported combinations above";
                        return;
                    }
                }

                this.loading = true;
                this.error = "";
                let toastoptions;
                try {
                    let credentialPayload = {
                        name: this.credential.name,
                        type: this.credTypeName,
                        fields: this.credentialType.fields.map((field) => {
                            field["value"] = this.credential[field.name]
                            return field
                        })
                    };
                    await this.createCredential(credentialPayload)
                    toastoptions = {
                        message: "Credentials added successfully.",
                        type: "success"
                    }

                } catch (error) {
                    this.error = error.response?.data?.error || error.message
                    toastoptions = {
                        message: this.error || "Something went wrong when adding the Credential. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch('addToast', toastoptions);
                    return
                } finally {
                    this.loading = false;
                }

                this.$store.dispatch('addToast', toastoptions);
                this.$store.dispatch('hideModal')
                this.$store.dispatch('amdp/fetchCredentials');
            } else {
                this.error = "'" + this.credential.name + "' name exits."
            }
        }
    }
};
</script>

<style>
/* Custom tooltip styling - not scoped so it can be applied to dynamically created elements */
.credential-tooltip {
    position: absolute;
    z-index: 9999;
    pointer-events: none;
    animation: fadeIn 0.2s ease-in-out;
    background-color: #6E84A3;
    color: #fff;
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    max-width: 400px;
    min-width: 300px;
    white-space: normal;
    display: block;
    line-height: 1.4;
}

.credential-tooltip ol {
    margin: 8px 0 0 0;
    padding-left: 20px;
}

.credential-tooltip .combo-item {
    margin: 4px 0;
    font-family: monospace;
    font-size: 11px;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(-5px);
    }

    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}
</style>